import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseAPI } from '@core/services/base-api.service';
import { BID_CHAT_API, BID_CHAT_HISTORY_FILTERS, BID_CHAT_LIMITS } from '@features/bid-chat/constants/bid-chat.constants';
import { ChatConversationDto, ChatConversationListDto, ChatFirebaseTokenDto } from '@features/bid-chat/models/bid-chat.model';

/** The mobile handoff documents a `result` envelope, the rest of this API uses `data`. */
const unwrap = <T>(res: { data?: T; result?: T }): T => res?.data ?? res?.result;

/**
 * The backend calls of the bid chat (#47189): the Firebase token, opening a conversation, History,
 * and accepting an offer. Everything else — Active chats, messages, sending, offers, unread
 * counters, live status — is Firestore.
 */
@Injectable({ providedIn: 'root' })
export class BidChatApiService {
  constructor(private baseAPI: BaseAPI) {}

  /** A Firebase custom token for the logged-in user, exchanged once for a Firebase session (chat contract §3.1). */
  getFirebaseToken(): Observable<ChatFirebaseTokenDto> {
    return this.baseAPI.get(BID_CHAT_API.FIREBASE_TOKEN).pipe(map((res) => unwrap<ChatFirebaseTokenDto>(res)));
  }

  /**
   * Opens the negotiation for a PENDING bid owned by the caller's shipment, on the customer's first
   * message. Idempotent: calling it again returns the conversation already open.
   * Errors: 403 UnauthorizedUser, 404 BidNotFound, 400 BidAlreadyProcessed.
   */
  initiateBidConversation(bidId: number): Observable<ChatConversationDto> {
    return this.baseAPI.post(BID_CHAT_API.INITIATE_BID, { bidId }).pipe(map((res) => unwrap<ChatConversationDto>(res)));
  }

  /**
   * History: the caller's CLOSED conversations, newest first (chat contract §4.3). First page only,
   * like the mobile app; `hasMore = offset + items.length < total` if paging is ever added.
   */
  getHistory(offset = 0): Observable<ChatConversationListDto> {
    return this.baseAPI
      .get(BID_CHAT_API.HISTORY, { params: { limit: BID_CHAT_LIMITS.HISTORY_LIMIT, offset, ...BID_CHAT_HISTORY_FILTERS } })
      .pipe(map((res) => unwrap<ChatConversationListDto>(res) ?? { items: [], total: 0 }));
  }

  /**
   * Accepts the fleet's active offer, by its Firestore ids. The backend does the rest — bid, shipment
   * and the offer's status in Firestore.
   * Errors: ChatOfferNotFound (no longer active), BidAlreadyProcessed / ShipmentNotBiddable (race).
   */
  acceptOffer(firestoreConversationId: string, messageId: string): Observable<void> {
    return this.baseAPI.post(BID_CHAT_API.ACCEPT_OFFER(firestoreConversationId, messageId), {});
  }
}
